import { Component, DestroyRef, inject, signal, OnInit, computed } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { SelectModule } from 'primeng/select';
import { TagModule } from 'primeng/tag';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService } from 'primeng/api';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import { SupplierService } from '../../core/services/supplier.service';
import { SupplierDto, SupplierDetailDto, PaymentMethod } from '../../core/models/finance.models';
import { MoneyPipe } from '../../shared/pipes/money.pipe';
import { CurrencyService } from '../../core/services/currency.service';

@Component({
  selector: 'app-suppliers',
  standalone: true,
  imports: [
    MoneyPipe,
    CommonModule,
    FormsModule,
    TableModule,
    ButtonModule,
    DialogModule,
    InputTextModule,
    InputNumberModule,
    SelectModule,
    TagModule,
    ConfirmDialogModule,
    TranslatePipe,
  ],
  providers: [ConfirmationService],
  templateUrl: './suppliers.component.html',
})
export class SuppliersComponent implements OnInit {
  private svc = inject(SupplierService);
  readonly currencySvc = inject(CurrencyService);
  private confirm = inject(ConfirmationService);
  private translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  suppliers = signal<SupplierDto[]>([]);
  loading = signal(false);
  search = '';
  onlyWithDebt = false;

  /**
   * Umumiy kreditorlik — HAR valyutada alohida (I11). Jamlangan bitta son ataylab
   * yo'q: `$700` va `1 260 000 so'm` ni qo'shib bo'lmaydi.
   */
  totalPayableByCurrency = computed(() => {
    const acc = new Map<string, number>();
    for (const s of this.suppliers()) {
      for (const b of s.balanceByCurrency ?? []) {
        if (b.balance > 0) acc.set(b.currency, (acc.get(b.currency) ?? 0) + b.balance);
      }
    }
    return [...acc.entries()]
      .map(([currency, amount]) => ({ currency, amount }))
      .sort((a, b) => b.amount - a.amount);
  });

  paymentMethods: { label: string; value: PaymentMethod }[] = [
    { label: 'FINANCE.METHOD.Cash', value: 'Cash' },
    { label: 'FINANCE.METHOD.Card', value: 'Card' },
    { label: 'FINANCE.METHOD.Bank', value: 'Bank' },
  ];

  // Create/Edit dialog
  editDialogVisible = false;
  editSaving = false;
  editId: string | null = null;
  form = { name: '', phoneNumber: '', address: '', notes: '', isActive: true };

  // Detail dialog
  detailDialogVisible = false;
  detailLoading = false;
  detail = signal<SupplierDetailDto | null>(null);

  // Payment dialog
  payDialogVisible = false;
  paySaving = false;
  paySupplier: SupplierDto | null = null;
  payAmount = 0;
  payMethod: PaymentMethod = 'Cash';
  /** Qaysi valyutadagi qarz to'lanyapti — yetkazib beruvchida bir nechtasi bo'lishi mumkin. */
  payCurrency = '';

  ngOnInit() {
    this.load();
  }

  load() {
    this.loading.set(true);
    this.svc
      .getAll(this.search || undefined, this.onlyWithDebt || undefined)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => this.suppliers.set(res.data ?? []),
        complete: () => this.loading.set(false),
      });
  }

  // ── Create / Edit ──────────────────────────────────────────────
  openCreate() {
    this.editId = null;
    this.form = { name: '', phoneNumber: '', address: '', notes: '', isActive: true };
    this.editDialogVisible = true;
  }

  openEdit(s: SupplierDto) {
    this.editId = s.id;
    this.form = {
      name: s.name,
      phoneNumber: s.phoneNumber,
      address: s.address ?? '',
      notes: s.notes ?? '',
      isActive: s.isActive,
    };
    this.editDialogVisible = true;
  }

  saveSupplier() {
    if (!this.form.name.trim()) {
      this.svc.showWarn(this.translate.instant('SUPPLIERS.VALIDATION.NAME_REQUIRED'));
      return;
    }
    this.editSaving = true;
    const done = {
      next: () => {
        this.svc.showSuccess();
        this.editDialogVisible = false;
        this.load();
      },
      error: () => (this.editSaving = false),
      complete: () => (this.editSaving = false),
    };
    if (this.editId) {
      this.svc
        .update(this.editId, { ...this.form })
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(done);
    } else {
      this.svc
        .create({ ...this.form })
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(done);
    }
  }

  deleteSupplier(s: SupplierDto) {
    this.confirm.confirm({
      message: this.translate.instant('SUPPLIERS.DELETE_CONFIRM', { name: s.name }),
      header: this.translate.instant('COMMON.CONFIRM'),
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.svc
          .delete(s.id)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => {
              this.svc.showSuccess();
              this.load();
            },
          });
      },
    });
  }

  // ── Detail ─────────────────────────────────────────────────────
  openDetail(s: SupplierDto) {
    this.detailDialogVisible = true;
    this.detailLoading = true;
    this.detail.set(null);
    this.svc
      .getById(s.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => this.detail.set(res.data),
        complete: () => (this.detailLoading = false),
      });
  }

  // ── Payment ────────────────────────────────────────────────────
  openPay(s: SupplierDto) {
    this.paySupplier = s;
    // Eng katta qarzi bor valyuta — odatda kassir shuni to'laydi.
    const top = (s.balanceByCurrency ?? []).filter((b) => b.balance > 0)[0];
    this.payCurrency = top?.currency ?? this.currencySvc.defaultCode();
    this.payAmount = top ? top.balance : s.balance > 0 ? s.balance : 0;
    this.payMethod = 'Cash';
    this.payDialogVisible = true;
  }

  confirmPay() {
    if (!this.paySupplier || this.payAmount <= 0) {
      this.svc.showWarn(this.translate.instant('SUPPLIERS.VALIDATION.AMOUNT'));
      return;
    }
    this.paySaving = true;
    this.svc
      .addPayment(this.paySupplier.id, {
        amount: this.payAmount,
        paymentMethod: this.payMethod,
        currency: this.payCurrency || null,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.svc.showSuccess();
          this.payDialogVisible = false;
          this.load();
        },
        error: () => (this.paySaving = false),
        complete: () => (this.paySaving = false),
      });
  }

  statusSeverity(status: string): 'success' | 'warn' | 'danger' {
    if (status === 'Paid') return 'success';
    if (status === 'PartiallyPaid') return 'warn';
    return 'danger';
  }
}
